import { formatDate } from 'my-core/date-utils';
import {
  studySessionAttachmentViewed,
  studySessionJoinNowClicked,
  studySessionRecordingViewed,
} from 'my-core/gtm-events';
import { useCurrentUser, useDialogStateManager } from 'my-core/hooks';
import {
  getAttachmentTypes,
  getLiveSessionDate,
  getStudySessionDateDateStatus,
  getStudySessionDateStatus,
  getStudySessionStartTime,
} from 'my-core/study-session-utils';

import DropDownButton from 'my-elements/DropDownButton';

import StudySessionVideoPlayer from './StudySessionVideoPlayer';

import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';

import { faArrowDownToLine, faVideo } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const BOOKLET_BUFFER = 24 * 3600 * 1000; // 24 hours
export default function StudySessionCardRegisteredActions({
  allDates,
  ButtonProps,
  className,
  course,
  dateStatus: dateStatusProp,
  studySession,
  studySessionDate,
  studySessionUser,
}) {
  const currentUser = useCurrentUser();

  const [videoDialogOpen, videoDialogProps, videoDialogActions] = useDialogStateManager();
  const dateStatus =
    dateStatusProp ||
    (studySessionDate && getStudySessionDateDateStatus(studySessionDate, studySession)) ||
    getStudySessionDateStatus(studySession);
  const liveSession = dateStatus === 'live' && (studySessionDate || (allDates && getLiveSessionDate(studySession)));

  const { booklets, recordings, solutions } = getAttachmentTypes(studySession, allDates ? 'all' : studySessionDate?.id);

  const bookletBuffer = studySession.disable_zoom ? 0 : BOOKLET_BUFFER;
  const shouldEnableBooklet = ssd => new Date(ssd.start_time).valueOf() < Date.now() + bookletBuffer;
  const canViewBooklet =
    !!booklets.length &&
    (!studySession.dates.length ||
      (studySessionDate ? shouldEnableBooklet(studySessionDate) : studySession.dates.some(shouldEnableBooklet)));

  const canViewSolutions = !!solutions.length && (dateStatus === 'past' || dateStatus === 'unknown');
  const { booklet_excluded, recording_excluded, solutions_excluded } = studySessionDate || studySession;
  const showJoinLink =
    (!studySession.disable_zoom || studySession.meeting_link) &&
    (studySessionDate || allDates) &&
    (dateStatus === 'upcoming' || liveSession);

  if (!showJoinLink && booklet_excluded && solutions_excluded && recording_excluded) return;

  const commonBtnProps = { size: 'small', ...ButtonProps };
  const commonLinkProps = {
    component: 'a',
    nativeButton: false,
    rel: 'noreferrer',
    target: '_blank',
  };
  const liveSessionButtonProps = {
    variant: 'contained',
    startIcon: <FontAwesomeIcon icon={faVideo} size="xs" />,
  };
  const pdfDowndloadButtonProps = {
    startIcon: <FontAwesomeIcon icon={faArrowDownToLine} size="xs" />,
  };

  let releaseDate =
    studySession.disable_zoom && (studySessionDate?.start_time || getStudySessionStartTime(studySession));
  if (releaseDate) {
    releaseDate = new Date(releaseDate);
    releaseDate = releaseDate > new Date() && releaseDate;
    releaseDate =
      releaseDate ? formatDate(releaseDate, 'fromNowStrict') : "very soon. You'll be notified when it is ready.";
  }

  return (
    <div
      className={className}
      css={({ spacing }) => ({
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: spacing(1),
        '.disabledBtnWrapper > *': { width: '100%' },
      })}
    >
      {showJoinLink &&
        (liveSession ? renderLiveSessionButton() : (
          renderDisabledButton(
            'You can join the session up to 1 hour before the scheduled time.',
            `Join ${formatDate(
              (studySessionDate || studySession.dates.find(d => new Date(d.start_time) > new Date())).start_time,
              'fromNowStrict',
            )}`,
            liveSessionButtonProps,
          )
        ))}
      {!booklet_excluded &&
        (canViewBooklet ?
          renderPdfButton(booklets, 'booklets', 'Booklet')
        : renderDisabledButton(
            `The PDF booklet will be available ${releaseDate || '24 hours before the start of the session'}`,
            'Booklet',
            pdfDowndloadButtonProps,
          ))}
      {!solutions_excluded &&
        (canViewSolutions ?
          renderPdfButton(solutions, 'solutions', 'Solutions')
        : renderDisabledButton(
            `The solutions will be available ${releaseDate || '24 hours after the end of the session'}`,
            'Solutions',
            pdfDowndloadButtonProps,
          ))}
      {!recording_excluded &&
        (studySession.disable_zoom || dateStatus === 'past') &&
        (recordings.length && ['past', 'unknown'].includes(dateStatus) ?
          renderVideoButton()
        : renderDisabledButton(
            `The recording will be available ${releaseDate || '24 hours after the end of the session'}`,
            'Watch Recording',
          ))}
      {videoDialogProps && (
        <StudySessionVideoPlayer
          attachment={videoDialogProps}
          onClose={videoDialogActions.close}
          open={videoDialogOpen}
          studySession={studySession}
          studySessionUser={studySessionUser}
          watermark={currentUser.email}
        />
      )}
    </div>
  );

  function renderDisabledButton(tooltipText, buttonText, buttonProps) {
    return (
      <Tooltip title={tooltipText}>
        <div className="disabledBtnWrapper">
          <Button {...commonBtnProps} disabled variant="outlined" {...buttonProps}>
            {buttonText}
          </Button>
        </div>
      </Tooltip>
    );
  }

  function renderLiveSessionButton() {
    const joinLink =
      (!studySession.disable_zoom && studySessionUser?.zoom_info?.[liveSession?.id]?.join_url) ||
      studySession.meeting_link;
    if (!joinLink)
      return renderDisabledButton(
        'There was an issue generating your Zoom link. Please contact support for assistance.',
        'Join Now',
        liveSessionButtonProps,
      );
    return (
      <Button
        {...commonBtnProps}
        {...liveSessionButtonProps}
        {...commonLinkProps}
        href={joinLink}
        onClick={() => studySessionJoinNowClicked({ course, ...studySession })}
      >
        Join Now
      </Button>
    );
  }

  function getPdfLink(attribute, attachment) {
    return `/api/v1/study_sessions/${studySession.id}/${attribute}/${attachment.attachment_id}${
      attribute === 'booklets' ? `/${encodeURIComponent(attachment.name)}` : ''
    }`;
  }

  function renderPdfButton(attachments, attribute, buttonLabel) {
    if (attachments.length === 1) {
      return (
        <Button
          {...commonBtnProps}
          {...commonLinkProps}
          {...pdfDowndloadButtonProps}
          href={getPdfLink(attribute, attachments[0])}
          onClick={() => studySessionAttachmentViewed({ course, ...studySession }, attachments[0])}
          variant="outlined"
        >
          {buttonLabel}
        </Button>
      );
    } else {
      return (
        <DropDownButton
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          button={
            <Button {...commonBtnProps} {...pdfDowndloadButtonProps} variant="outlined">
              {buttonLabel}
            </Button>
          }
          menu
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {attachments.map(att => (
            <MenuItem
              key={att.id}
              {...commonLinkProps}
              href={getPdfLink(attribute, att)}
              onClick={() => studySessionAttachmentViewed({ course, ...studySession }, att)}
            >
              {att.name}
            </MenuItem>
          ))}
        </DropDownButton>
      );
    }
  }

  function recordingBtnProps(att) {
    return {
      onClick: () => {
        studySessionRecordingViewed({ course, ...studySession }, att);
        videoDialogActions.open(att);
      },
    };
  }

  function renderVideoButton() {
    if (recordings.length === 1) {
      return (
        <Button {...commonBtnProps} variant="outlined" {...recordingBtnProps(recordings[0])}>
          Watch Recording
        </Button>
      );
    } else {
      return (
        <DropDownButton
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          button={
            <Button {...commonBtnProps} variant="outlined">
              Watch Recording
            </Button>
          }
          menu
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {recordings.map(att => (
            <MenuItem key={att.id} {...recordingBtnProps(att)}>
              {att.name}
            </MenuItem>
          ))}
        </DropDownButton>
      );
    }
  }
}
